var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { msg, str, localized } from '@lit/localize';
import '../icon/Icon.js';
/**
 * Inline SVG paths and circles for the four supported transport profiles.
 * Kept here so the component is self-contained — callers just pass the
 * `profile` string. Lucide-style line icons.
 */
const PROFILE_ICON_PATHS = {
    car: 'M14 16H9m10 0h3v-3.15a1 1 0 0 0-.84-.99L16 11l-2.7-3.6a1 1 0 0 0-.8-.4H5.24a2 2 0 0 0-1.8 1.1l-.8 1.63A6 6 0 0 0 2 12.42V16h2',
    truck: 'M5 18H3c-.6 0-1-.4-1-1V7c0-.6.4-1 1-1h10c.6 0 1 .4 1 1v11M14 9h4l4 4v4c0 .6-.4 1-1 1h-2',
    bike: 'M15 6a1 1 0 1 0 0-2 1 1 0 0 0 0 2zM12 17.5V14l-3-3 5-4 2 3h2',
    pedestrian: 'M15 22V11l-3-3-3 4 3 3v7M9 12l-3 4M14 14l3 1 2 4',
};
const PROFILE_ICON_CIRCLES = {
    car: [
        [6.5, 16.5, 2.5],
        [16.5, 16.5, 2.5],
    ],
    truck: [
        [7, 18, 2],
        [17, 18, 2],
    ],
    bike: [
        [5.5, 17.5, 3.5],
        [18.5, 17.5, 3.5],
    ],
    pedestrian: [[13, 4, 2]],
};
/**
 * Compact zone row for a scenario / step-zone list. Renders:
 *   [eye toggle] [color swatch] [name] [profile icon] [value label]
 *
 * Emits `visibility-toggle` with `{ zone_id, hidden }` on eye click.
 *
 * Use this for the scenario zone list view; the heavier `<zone-card>`
 * (color picker, type badge, description) is the admin-side card.
 */
let ScenarioZoneCard = class ScenarioZoneCard extends LitElement {
    constructor() {
        super(...arguments);
        this.name = null;
        this.color = null;
        this.profile = null;
        this.valueLabel = null;
        this.hidden = false;
        this.disabled = false;
    }
    toggle() {
        if (this.disabled)
            return;
        const nextHidden = !this.hidden;
        this.dispatchEvent(new CustomEvent('visibility-toggle', {
            detail: { zone_id: this.zoneId, hidden: nextHidden },
            bubbles: true,
            composed: true,
        }));
    }
    renderEye() {
        return html `<basic-icon
      .name=${this.hidden ? 'eye-off' : 'eye'}
      size="16"
    ></basic-icon>`;
    }
    renderProfileIcon() {
        if (!this.profile)
            return nothing;
        const path = PROFILE_ICON_PATHS[this.profile];
        if (!path)
            return nothing;
        const circles = PROFILE_ICON_CIRCLES[this.profile] ?? [];
        return html `<span class="profile-icon" title=${this.profile}>
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d=${path}></path>
        ${circles.map(([cx, cy, r]) => html `<circle cx=${cx} cy=${cy} r=${r}></circle>`)}
      </svg>
    </span>`;
    }
    render() {
        const displayName = this.name && this.name.trim()
            ? this.name
            : msg(str `Zone #${this.zoneId}`, {
                id: 'zoneCard.fallbackName',
                desc: 'Fallback display name for a zone with no name, using its id',
            });
        const swatchColor = this.color ?? '#888';
        return html `
      <div class="row">
        <button
          class="visibility-toggle"
          type="button"
          ?disabled=${this.disabled}
          aria-pressed=${this.hidden ? 'false' : 'true'}
          title=${this.hidden
            ? msg('Show on map', { id: 'card.visibility.show', desc: 'Tooltip for the button that makes a zone visible on the map' })
            : msg('Hide from map', { id: 'card.visibility.hide', desc: 'Tooltip for the button that hides a zone from the map' })}
          @click=${this.toggle}
        >
          ${this.renderEye()}
        </button>
        <span class="swatch" style="background:${swatchColor}"></span>
        <span class="name" title=${displayName}>${displayName}</span>
        <span class="meta">
          ${this.renderProfileIcon()}
          ${this.valueLabel
            ? html `<span>${this.valueLabel}</span>`
            : nothing}
        </span>
      </div>
    `;
    }
};
ScenarioZoneCard.styles = css `
    :host {
      display: block;
    }

    .row {
      display: grid;
      grid-template-columns: 22px 14px 1fr auto;
      align-items: center;
      gap: 10px;
      padding: 10px 12px;
      border: 1px solid #eee;
      border-radius: 6px;
      background: #fafafa;
      box-sizing: border-box;
    }

    :host([hidden]) .swatch,
    :host([hidden]) .name,
    :host([hidden]) .meta {
      opacity: 0.45;
    }

    .visibility-toggle {
      background: transparent;
      border: none;
      padding: 0;
      cursor: pointer;
      color: #666;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 22px;
      height: 22px;
      border-radius: 3px;
    }
    .visibility-toggle:hover:not(:disabled) {
      color: #1a1a1a;
      background: rgba(0, 0, 0, 0.04);
    }
    .visibility-toggle:disabled {
      cursor: not-allowed;
    }
    :host([hidden]) .visibility-toggle {
      color: #b0b0b0;
    }
    .visibility-toggle svg {
      width: 16px;
      height: 16px;
    }

    .swatch {
      width: 14px;
      height: 14px;
      border-radius: 3px;
      border: 1px solid rgba(0, 0, 0, 0.12);
    }

    .name {
      font-size: 13px;
      color: #333;
      font-family: inherit;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .meta {
      font-size: 12px;
      color: #888;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      font-family: inherit;
    }

    .profile-icon {
      display: inline-flex;
      color: #555;
    }
    .profile-icon svg {
      width: 14px;
      height: 14px;
    }
  `;
__decorate([
    property({ type: Number, attribute: 'zone-id' })
], ScenarioZoneCard.prototype, "zoneId", void 0);
__decorate([
    property({ type: String })
], ScenarioZoneCard.prototype, "name", void 0);
__decorate([
    property({ type: String })
], ScenarioZoneCard.prototype, "color", void 0);
__decorate([
    property({ type: String })
], ScenarioZoneCard.prototype, "profile", void 0);
__decorate([
    property({ type: String, attribute: 'value-label' })
], ScenarioZoneCard.prototype, "valueLabel", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], ScenarioZoneCard.prototype, "hidden", void 0);
__decorate([
    property({ type: Boolean })
], ScenarioZoneCard.prototype, "disabled", void 0);
ScenarioZoneCard = __decorate([
    localized(),
    customElement('scenario-zone-card')
], ScenarioZoneCard);
export { ScenarioZoneCard };
//# sourceMappingURL=ScenarioZoneCard.js.map